import { Icon, type IconicNames } from '@clubmed/trident-icons';
import clsx from 'clsx';
import type { ComponentPropsWithoutRef, FunctionComponent } from 'react';

import {
  type BgColor,
  type BorderColor,
  getBgColor,
  getBorderColor,
  getTextColor,
  type TextColor,
} from './helpers/colors/colors';
import type { LiteralUnion } from './types/LiteralUnion';
import type { IconProps } from '@clubmed/trident-icons';
import { twMerge } from './helpers/twMerge';

export interface TagProps extends Omit<ComponentPropsWithoutRef<'span'>, 'color'> {
  /**
   * Additional class names
   */
  className?: string;
  /**
   * Background color
   */
  backgroundColor?: LiteralUnion<BgColor>;
  /**
   * Text color
   */
  color?: LiteralUnion<TextColor>;
  /**
   * Border color
   */
  border?: LiteralUnion<BorderColor>;
  /**
   * Optional icon name
   */
  icon?: IconicNames;
  /**
   * Tag label
   */
  label?: string;
  /**
   * theme mode
   */
  theme?: 'icon' | 'label' | 'both' | 'swap' | 'unfold' | 'monogram' | 'pill' | 'none';
  /**
   * Icon class name
   */
  iconClassName?: string;
  iconWidth?: IconProps['width'];
  /**
   * Label class name
   */
  labelClassName?: string;
}

export const Tag: FunctionComponent<TagProps> = ({
  className,
  backgroundColor = 'saffron',
  border = backgroundColor,
  color = 'black',
  icon,
  label = '',
  theme = 'both',
  iconClassName,
  labelClassName,
  iconWidth,
  ...attrs
}) => {
  if (!label && !icon) {
    return null;
  }

  const borderColor = getBorderColor(border);
  const bgColor = getBgColor(backgroundColor);
  const textColor = getTextColor(color);

  const hasIcon = !!icon;
  const mode = theme === 'both' && !hasIcon ? 'label' : theme;

  const withBoth = mode === 'both' || mode === 'swap' || mode === 'unfold' || mode === 'none';
  const withIcon = hasIcon && (mode === 'icon' || withBoth);
  const withLabel = mode === 'label' || mode === 'pill' || withBoth;

  return (
    <span
      role="note"
      {...attrs}
      data-name="Tag"
      className={twMerge(
        clsx(
          'box-border inline-flex items-center justify-center gap-x-4 whitespace-nowrap rounded-pill border align-middle font-sans font-semibold',
          {
            'text-b4': mode !== 'pill',
            'text-b5': mode === 'pill',
          },
          {
            'p-4': mode !== 'label' && mode !== 'both',
            'px-16 py-6': mode === 'label',
            'py-4 pe-16 ps-12': mode === 'both',
            'px-8 py-4': mode === 'pill',
            'sm:px-16': mode === 'swap',
            'sm:pe-16 sm:ps-12': mode === 'unfold',
          },
          borderColor,
          bgColor,
          textColor,
        ),
        className,
      )}
      title={label}
    >
      {mode === 'monogram' && <span className="size-24 text-center leading-[1.5rem]">{label}</span>}
      {withIcon && (
        <Icon
          width={iconWidth ?? '1.5rem'}
          name={icon}
          className={clsx({ 'sm:hidden': mode === 'swap' }, iconClassName)}
        />
      )}
      {withLabel && (
        <span
          className={clsx(
            {
              'hidden sm:block': mode === 'swap' || mode === 'unfold',
            },
            labelClassName,
          )}
        >
          {label}
        </span>
      )}
    </span>
  );
};
